import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Input from '@/components/common/Input';
import PasswordInput from '@/components/common/PasswordInput';
import Button from '@/components/common/Button';
import { validatePassword } from '@/utils/validation';

export default function ResetPasswordPage() {
  const { resetPassword, error, clearError } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordValidation = validatePassword(newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (newPassword !== confirmPassword) return;

    setIsSubmitting(true);
    try {
      await resetPassword(email, code, newPassword);
    } catch {
      // Error is set in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h2 className="text-xl font-semibold text-neutral-900">Reset Password</h2>
      <p className="text-sm text-neutral-600">
        Enter the code from your email and choose a new password.
      </p>

      {error && (
        <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700" role="alert">
          {error}
        </div>
      )}

      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
        autoComplete="email"
      />

      <Input
        label="Verification Code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder=""
        required
        autoComplete="one-time-code"
      />

      <PasswordInput
        label="New Password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        placeholder=""
        required
        autoComplete="new-password"
        error={newPassword && !passwordValidation.isValid ? passwordValidation.errors[0] : undefined}
      />

      <PasswordInput
        label="Confirm New Password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        placeholder=""
        required
        autoComplete="new-password"
        error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
      />

      <Button
        type="submit"
        className="w-full"
        loading={isSubmitting}
        disabled={!passwordValidation.isValid || newPassword !== confirmPassword}
      >
        Reset Password
      </Button>

      <Link
        to="/login"
        className="block text-center text-sm text-primary-600 hover:text-primary-700"
      >
        Back to Sign In
      </Link>
    </form>
  );
}
